import React, { useState, useEffect, useCallback } from "react";
import * as S from "./style";
import { 
  Banknote, 
  CheckCircle, 
  Clock, 
  Download 
} from "lucide-react";
import Table from "@components/table";
import { Reports, Zone } from "@apis/index";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import useConfig from "@hooks/useConfig";
import { useNavigate } from "react-router-dom";
import { APP_BASE_URL, API_KEY } from "@utils/request";
import { toast } from "react-toastify";

const DisbursementReport = () => {
  const navigate = useNavigate();
  const { bucketUrl } = useConfig();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { settings: currencySettings } = useGeneralSettings();
  const [exporting, setExporting] = useState(false);

  const { data: modules } = useSelector((state: RootState) => state.modules);
  const { data: zones } = useSelector((state: RootState) => state.zones);

  useEffect(() => {
    Zone.list({ state: "zones" });
  }, []);

  const [filters, setFilters] = useState({
    search: "",
    role: "All Roles",
    status: "All Statuses",
    moduleId: "",
    zoneId: "",
    dateRange: "",
    startDate: "",
    endDate: "",
  });

  const [summary, setSummary] = useState<any>(null);

  const fetchDisbursements = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await Reports.getDisbursementReport({
        params: {
          page,
          limit: 10,
          ...filters,
          role: filters.role === "All Roles" ? "" : filters.role,
          status: filters.status === "All Statuses" ? "" : filters.status,
        },
      });
      if (res) {
        setData(res.data || []);
        setSummary(res.summary || null);
        setTotalItems(res.meta?.total || 0);
        setTotalPages(res.meta?.totalPages || 0);
      }
    } catch (error) {
      console.error("Failed to fetch disbursements:", error);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchDisbursements();
  }, [fetchDisbursements]);

  const handleFilterChange = (key: string, value: string) => {
    let updates: any = { [key]: value };
    
    if (key === "dateRange") {
      const today = new Date();
      if (value === "today") {
        updates.startDate = today.toISOString().split("T")[0];
        updates.endDate = updates.startDate;
      } else if (value === "this_week") {
        const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
        updates.startDate = firstDay.toISOString().split("T")[0];
        updates.endDate = new Date().toISOString().split("T")[0];
      } else if (value === "this_month") {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        updates.startDate = firstDay.toISOString().split("T")[0];
        updates.endDate = new Date().toISOString().split("T")[0];
      } else if (value === "this_year") {
        const firstDay = new Date(today.getFullYear(), 0, 1);
        updates.startDate = firstDay.toISOString().split("T")[0];
        updates.endDate = new Date().toISOString().split("T")[0];
      } else if (value === "custom") {
        // preserve current or let user pick
      } else {
        updates.startDate = "";
        updates.endDate = "";
      }
    }
    
    setFilters(prev => ({ ...prev, ...updates }));
    setPage(1);
  };

  const exportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    const toastId = toast.info("Generating Excel report, please wait...", { autoClose: false });
    try {
      const user = Helper.getUser();
      const token = user?.token;
      
      const queryParams = new URLSearchParams();
      if (filters.search) queryParams.append("search", filters.search);
      if (filters.role && filters.role !== "All Roles") queryParams.append("role", filters.role);
      if (filters.status && filters.status !== "All Statuses") queryParams.append("status", filters.status);
      if (filters.moduleId) queryParams.append("moduleId", filters.moduleId);
      if (filters.zoneId) queryParams.append("zoneId", filters.zoneId);
      if (filters.startDate) queryParams.append("startDate", filters.startDate);
      if (filters.endDate) queryParams.append("endDate", filters.endDate);
      
      const downloadUrl = `${APP_BASE_URL}/admin/reports/disbursements/export?${queryParams.toString()}`;
      
      const response = await fetch(downloadUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Api-Key": API_KEY,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to export Excel report");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `disbursement_report_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      
      toast.update(toastId, {
        render: "Excel report exported successfully!",
        type: "success",
        autoClose: 3000,
      });
    } catch (error: any) {
      console.error("Export failed:", error);
      toast.update(toastId, {
        render: error?.message || "Failed to export Excel report",
        type: "error",
        autoClose: 4000,
      });
    } finally {
      setExporting(false);
    }
  };

  const stats = [
    { label: "Total Disbursed", value: Helper.formatCurrency(summary?.totalPaid || 0, currencySettings), Icon: Banknote, color: "#1890ff", bgColor: "#e6f7ff" },
    { label: "Total Pending", value: Helper.formatCurrency(summary?.totalPending || 0, currencySettings), Icon: Clock, color: "#faad14", bgColor: "#fffbe6" },
    { label: "Total Records", value: totalItems.toLocaleString(), Icon: CheckCircle, color: "#52c41a", bgColor: "#f6ffed" },
  ];

  const columns = [
    { title: "ID", key: "id" },
    { 
      title: "Date", 
      key: "date",
      render: (item: any) => new Date(item.date).toLocaleDateString()
    },
    { 
      title: "Recipient", 
      key: "recipient",
      render: (item: any) => {
        const imageUrl = item.avatar ? `${bucketUrl}/${item.avatar}` : null;
        const name = item.role === 'store' ? item.storeName : item.recipient;
        const handleNavigate = () => {
          if (item.role === 'store') {
            navigate(`/admin/users/store/store-lists/view/${item.userId}`);
          }
        };
        return (
          <S.ProfileCard 
            onClick={handleNavigate} 
            style={{ cursor: item.role === 'store' ? "pointer" : "default" }}
          >
            {imageUrl ? (
              <S.ProfileAvatar 
                src={imageUrl} 
                alt={name}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = "flex";
                }}
              />
            ) : null}
            <S.ProfileAvatarPlaceholder 
              variant={item.role === 'store' ? 'store' : 'customer'}
              style={{ display: imageUrl ? "none" : "flex" }}
            >
              {name?.charAt(0).toUpperCase()}
            </S.ProfileAvatarPlaceholder>
            <S.ProfileDetails>
              <S.ProfileName>{name}</S.ProfileName>
              <S.ProfileSubtext>{item.email || item.phoneNo}</S.ProfileSubtext>
            </S.ProfileDetails>
          </S.ProfileCard>
        )
      }
    },
    { 
      title: "Role", 
      key: "role",
      render: (item: any) => <span style={{ textTransform: 'capitalize' }}>{item.role}</span>
    },
    { 
      title: "Amount Info", 
      key: "amount",
      render: (item: any) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600 }}>{Helper.formatCurrency(item.amount, currencySettings)}</span>
          {item.paidAmount > 0 && (
            <span style={{ fontSize: '11px', color: '#52c41a' }}>
              Paid: {Helper.formatCurrency(item.paidAmount, currencySettings)}
            </span>
          )}
        </div>
      )
    },
    { title: "Method", key: "method" },
    { 
      title: "Status", 
      key: "status",
      render: (item: any) => {
        const isPaid = item.status === 'Approved' || item.status === 'Paid';
        const isPending = item.status === 'Pending';
        return (
          <S.StatusPill 
            bg={isPaid ? '#f6ffed' : isPending ? '#fffbe6' : '#fff1f0'}
            color={isPaid ? '#52c41a' : isPending ? '#faad14' : '#ff4d4f'}
          >
            {item.status}
          </S.StatusPill>
        );
      }
    },
    {
      title: "Notes / TX",
      key: "transactionId",
      render: (item: any) => (
        <div style={{ fontSize: '11px', color: '#666', maxWidth: '150px' }}>
          {item.transactionId && <div style={{ fontWeight: 500 }}>TX: {item.transactionId}</div>}
          {item.adminNote && <div>Note: {item.adminNote}</div>}
        </div>
      )
    }
  ];

  return (
    <S.Container>
      <S.Header>
        <S.Title>Disbursement Report</S.Title>
        <S.SubTitle>Monitor and track payouts to stores and delivery partners.</S.SubTitle>
      </S.Header>

      <S.StatsGrid>
        {stats.map((stat, i) => (
          <S.StatCard key={i} color={stat.color}>
            <S.StatIcon color={stat.color} bgColor={stat.bgColor}>
              <stat.Icon size={24} />
            </S.StatIcon>
            <S.StatInfo>
              <S.StatValue>{stat.value}</S.StatValue>
              <S.StatLabel>{stat.label}</S.StatLabel>
            </S.StatInfo>
          </S.StatCard>
        ))}
      </S.StatsGrid>

      <S.FilterSection>
        <S.FilterItem>
          <S.Label>Recipient Name</S.Label>
          <S.Input 
            type="text" 
            placeholder="Enter name..." 
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
          />
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Role</S.Label>
          <S.Select 
            value={filters.role}
            onChange={(e) => handleFilterChange("role", e.target.value)}
          >
            <option>All Roles</option>
            <option value="store">Store</option>
            <option value="delivery">Delivery Partner</option>
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Payment Status</S.Label>
          <S.Select 
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <option>All Statuses</option>
            <option value="Approved">Paid/Approved</option>
            <option value="Pending">Pending</option>
            <option value="Rejected">Rejected</option>
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Module</S.Label>
          <S.Select 
            value={filters.moduleId}
            onChange={(e) => handleFilterChange("moduleId", e.target.value)}
          >
            <option value="">All Modules</option>
            {modules?.map((m: any) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Zone</S.Label>
          <S.Select 
            value={filters.zoneId}
            onChange={(e) => handleFilterChange("zoneId", e.target.value)}
          >
            <option value="">All Zones</option>
            {zones?.map((z: any) => (
              <option key={z.id} value={z.id}>{z.name}</option>
            ))}
          </S.Select>
        </S.FilterItem>
        <S.FilterItem>
          <S.Label>Time Frame</S.Label>
          <S.Select 
            value={filters.dateRange}
            onChange={(e) => handleFilterChange("dateRange", e.target.value)}
          >
            <option value="">All Time</option>
            <option value="today">Today</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="this_year">This Year</option>
            <option value="custom">Custom Time</option>
          </S.Select>
        </S.FilterItem>
        {filters.dateRange === "custom" && (
          <>
            <S.FilterItem>
              <S.Label>Start Date</S.Label>
              <S.Input 
                type="date" 
                value={filters.startDate}
                onChange={(e) => handleFilterChange("startDate", e.target.value)}
              />
            </S.FilterItem>
            <S.FilterItem>
              <S.Label>End Date</S.Label>
              <S.Input 
                type="date" 
                value={filters.endDate}
                onChange={(e) => handleFilterChange("endDate", e.target.value)}
              />
            </S.FilterItem>
          </>
        )}
      </S.FilterSection>

      <S.TableWrapper>
        <S.TableHeader>
          <S.TableTitle>Disbursement History ({totalItems})</S.TableTitle>
          <S.ExportButton onClick={exportExcel} disabled={exporting}>
            <Download size={16} /> {exporting ? "Exporting..." : "Export Excel"}
          </S.ExportButton>
        </S.TableHeader>
        <Table 
          columns={columns} 
          data={data} 
          loading={loading}
          totalPages={totalPages}
          onPageChange={setPage}
          pageControlled={page}
        />
      </S.TableWrapper>
    </S.Container>
  );
};

export default DisbursementReport;
